<!-- With the pick segment: wide enough for three segments, as tall as the viewport allows up to the design's 832px, and a bottom sheet on a phone. -->
<dm-modal
  [size]="hasPickSegment() ? 'lg' : 'md'"
  [align]="hasPickSegment() ? 'top' : 'center'"
  [height]="hasPickSegment() ? 'fill' : 'content'"
  [style.--dm-modal-max-block-size]="hasPickSegment() ? '52rem' : null"
  [mobileLayout]="hasPickSegment() ? 'sheet' : 'inset'"
  [swipe]="hasPickSegment()"
  [swipeHandle]="hasPickSegment()"
  [open]="isModalOpen()"
  [canautoclose]="false"
  [withclickoutside]="false"
  (keydown)="onKeydown($event)"
  (dmModalCloseClick)="onCloseClick()"
  (dmOpenChange)="onOpenChange($event)"
  (dmModalClosed)="onModalClosed()"
>
  @if (contentMounted()) {
    <dm-modal-header slot="header" [heading]="headingText()" [description]="descriptionText()">
      <!-- As a phone's sheet the picker dialog needs every line for the list, so it drops the icon there. -->
      <dm-icon
        slot="icon"
        [class]="hasPickSegment() ? 'dm-none mobile-lg:dm-flex' : ''"
        [featured]="true"
        theme="modern"
        color="gray"
        size="lg"
        name="plus-circle"
      />
    </dm-modal-header>

    <!-- Alternatives, not a filter: switching drops the other segments' input. Where they do not fit, they become a select. -->
    <dm-button-group
      size="sm"
      selectionmode="single"
      overflow="select"
      class="dm-text-align-left"
      [ariaLabel]="segmentsLabel"
      [activeindex]="activeSegmentIndex()"
    >
      @for (segment of segments(); track segment.modus) {
        <dm-button-group-item [active]="modus() === segment.modus" (click)="onModusChange(segment.modus)">
          {{ segment.label }}
        </dm-button-group-item>
      }
    </dm-button-group>

    @if (modus() === 'pick') {
      <!-- Outside the form, so Enter in the picker's search does not submit the selection. -->
      <div class="dm-margin-top-20 dm-text-align-left">
        <ng-container [ngTemplateOutlet]="pickSegment()?.templateRef ?? null" />
      </div>
    } @else {
      <form [formRoot]="uploadForm" class="dm-text-align-left">
        @if (modus() === 'datei') {
          <div class="dm-margin-top-20">
            <!-- `multiple` for its layout — the progress rows sit below the dropzone, as the
               design shows. Whether a second file joins the first or replaces it is the
               dialog's `multipleFiles`, through the `files` it hands back. On a create each
               row names its own document. -->
            <dm-fileupload
              [multiple]="true"
              [editablenames]="namesInRows()"
              variant="bar"
              outputformat="file"
              [files]="files()"
              [mimetypes]="mimetypes()"
              [maxsize]="maxDateigroesse()"
              [hint]="dropzoneHint()"
              [uploadtext]="uploadCtaLabel"
              [dragdroptext]="dragDropLabel"
              [uploadinglabel]="uploadingLabel"
              [completelabel]="completeLabel"
              [failedlabel]="failedLabel"
              [failedmessage]="failedMessage"
              [tryagainlabel]="tryAgainLabel"
              [deletelabel]="deleteFileLabel"
              [renamelabel]="renameFileLabel"
              (dmFileuploadFileChange)="onFileChange($event.detail)"
              (dmFileuploadFileDelete)="onFileDelete($event.detail)"
              (dmFileuploadFileRename)="onFileRename($event.detail)"
              (dmFileUploadTryAgainClick)="onRetry($event.detail)"
              (dmFileuploadSizeLimitExceed)="onSizeLimitExceeded()"
              (dmFileuploadUnacceptedFiles)="onUnacceptedFile()"
            />
          </div>

          @if (duplicateWarning()) {
            <div class="dm-margin-top-16">
              <dm-alert
                color="warning"
                [tinted]="true"
                [closable]="true"
                icon="alert-circle"
                [heading]="duplikatHeading"
                [text]="duplikatText()"
                [dismissbuttonlabel]="duplikatSchliessenLabel"
                (dmAlertCloseClick)="onDuplikatAkzeptiert()"
              >
                <dm-button slot="actions" color="link-gray" size="sm" (click)="onDuplikatAkzeptiert()">
                  {{ duplikatSchliessenLabel }}
                </dm-button>
                <dm-button slot="actions" color="link-color" size="sm" (click)="onDuplicatesDelete()">
                  {{ duplikatEntfernenLabel() }}
                </dm-button>
              </dm-alert>
            </div>
          }
        } @else {
          <div class="dm-margin-top-20">
            <dm-input
              [label]="dateipfadLabel"
              dmSize="md"
              inputtype="url"
              inputId="externer-link"
              [required]="true"
              [tooltip]="dateipfadTooltip"
              [placeholder]="dateipfadPlaceholder"
              [value]="uploadForm.externerLink?.()?.value() || ''"
              (dmInput)="onExternerLinkChange($event.detail.value?.toString() ?? '')"
            />
          </div>
        }

        <!-- A file being created is named in its row, so the name field serves only a link and an edit.
             Several files make several documents, so there is no one description to type; the tags and
             types below apply to all of them. -->
        @if (!hasSeveralFiles()) {
          @if (!namesInRows()) {
            <div class="dm-margin-top-20">
              <dm-input
                [label]="dateinameLabel"
                dmSize="md"
                type="textarea"
                inputId="dateiname"
                [rows]="1"
                [autosize]="true"
                [singleline]="true"
                [errormsg]="dateinameErrorMessage()"
                [formField]="uploadForm.dateiname"
              />
            </div>
          }

          <div class="dm-margin-top-20">
            <dm-input
              [label]="descriptionLabel"
              dmSize="md"
              type="textarea"
              inputId="beschreibung"
              [rows]="3"
              [placeholder]="descriptionPlaceholder"
              [value]="uploadForm.beschreibung?.()?.value() || ''"
              (dmInput)="onDescriptionChange($event.detail.value?.toString() ?? '')"
            />
          </div>
        }

        <div class="dm-margin-top-20">
          <!-- Searchable against the customer's own vocabulary, and `allowCustomValue` turns a
             term nobody used yet into a new tag — which is what the story asks for and what
             `dm-input-tags` could not do, having no autocomplete. -->
          <dm-combobox
            variant="chips"
            size="md"
            [label]="tagsLabel"
            [tooltip]="tagsTooltip"
            [hint]="tagsHint()"
            [placeholder]="tagsPlaceholder"
            [multiple]="true"
            [dynamicSearch]="true"
            [allowCustomValue]="true"
            [createLabel]="tagCreateLabel"
            [loading]="isTagsLoading()"
            [filterDebounce]="environmentUser.uiDebounceTime"
            [emptyStateTitle]="noTagsTitle"
            [emptyStateDescription]="noTagsDescription"
            [value]="tagNames()"
            [showAllSelectedState]="false"
            (dmInput)="searchTags($event.detail)"
            (dmCreate)="onTagCreate($event.detail)"
            (dmChange)="onTagsChange($event.detail.values)"
          >
            @for (tag of tagOptions(); track tag.tagID) {
              <dm-select-item [value]="tag.name">#{{ tag.name }}</dm-select-item>
            }
          </dm-combobox>
        </div>

        @if (isAdmin()) {
          <div class="dm-margin-top-20">
            <dm-select
              size="md"
              [label]="dokumentTypLabel"
              [placeholder]="dokumentTypLabel"
              [value]="dokumentTypID() ?? ''"
              (dmChange)="onDokumentTypChange($event.detail.value)"
            >
              <dm-select-item value="" [label]="noDokumentTypLabel" />
              @for (option of dokumentTypOptions(); track option.value) {
                <dm-select-item [value]="option.value" [label]="option.label" />
              }
            </dm-select>
          </div>

          @if (dokumentSubTypOptions().length > 0) {
            <div class="dm-margin-top-20">
              <dm-select
                size="md"
                [label]="dokumentSubTypLabel"
                [placeholder]="dokumentSubTypLabel"
                [value]="dokumentSubTypID() ?? ''"
                (dmChange)="onDokumentSubTypChange($event.detail.value)"
              >
                <dm-select-item value="" [label]="noDokumentTypLabel" />
                @for (option of dokumentSubTypOptions(); track option.value) {
                  <dm-select-item [value]="option.value" [label]="option.label" />
                }
              </dm-select>
            </div>
          }
        }
      </form>
    }

    <dm-modal-footer slot="footer" layout="horizontal-fill">
      <!-- `dmType="button"` and a direct `save()`: the footer is slotted into `dm-modal`, so
           it sits outside the `<form>` and a submit button would have no form to submit.
           `[formRoot]` still carries the action, which is what makes Enter in a field save. -->
      <dm-button color="secondary" size="md" dmType="button" (click)="onCloseClick()">{{ cancelLabel }}</dm-button>
      <dm-button color="primary" size="md" dmType="button" [disabled]="!canSave()" [isLoading]="isSaving()" (click)="save()">
        {{ primaryLabel() }}
      </dm-button>
    </dm-modal-footer>
  }
</dm-modal>
